'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Sun, Moon, Bell, User, Languages, MessageCircle, UsersRound, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useThemeStore, useLangDataStore, useNotificationStore, useUserStore } from '@/utils/util_states';
import { loadNotifications } from '@/services/account';
import { getNotificationCopy } from '@/utils/notification-copy';
import { LANGUAGE_OPTIONS, localizedPublicPath, stripPublicLocale } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';

// ─────────────────────────────────────────────
// Route → page title mapping (computed inside component for i18n)
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// Topbar Component (64px)
// ─────────────────────────────────────────────
type TopbarProps = {
  languageCode: string;
  notificationDrawerOpen: boolean;
  onOpenNotifications: () => void;
};

export default function Topbar({ languageCode, notificationDrawerOpen, onOpenNotifications }: TopbarProps) {
  const pathname = usePathname();
  const basePathname = stripPublicLocale(pathname);
  const { theme, setTheme } = useThemeStore();
  const { updateLanguageCode } = useLangDataStore();
  const langData = getLanguageDataByCode(languageCode);
  const { isLogin, authReady, userProfile } = useUserStore();
  const { unreadCount, setUnreadCount, clearUnreadCount } = useNotificationStore();
  const [communityOpen, setCommunityOpen] = useState(false);
  const communityRef = useRef<HTMLDivElement>(null);
  const qqGroupUrl = process.env.NEXT_PUBLIC_QQ_GROUP_URL?.trim() || 'https://qm.qq.com/q/5TwUKZPoIM';

  useEffect(() => {
    function closeCommunity(event: PointerEvent) {
      if (!communityRef.current?.contains(event.target as Node)) setCommunityOpen(false);
    }
    document.addEventListener('pointerdown', closeCommunity);
    return () => document.removeEventListener('pointerdown', closeCommunity);
  }, []);

  const pageTitles: Record<string, string> = {
    '/discover': langData.nav_discover,
    '/guide/haiguitang': languageCode === 'zh-CN' ? '海龟汤指南' : 'Guide',
    '/puzzles': langData.nav_my_puzzles,
    '/create': langData.nav_create,
    '/profile': langData.nav_profile,
    '/recharge': langData.nav_recharge,
    '/settings': langData.nav_settings,
    '/privacy': langData.settings_privacy_policy,
    '/terms': langData.settings_terms_of_service,
    '/support': languageCode.toLowerCase().startsWith('zh') ? '客服与帮助' : 'Support',
    '/notifications': getNotificationCopy(languageCode, langData).notif_page_title,
  };

  useEffect(() => {
    if (!authReady) return;
    if (!isLogin) {
      clearUnreadCount();
      return;
    }
    let cancelled = false;
    void loadNotifications()
      .then((result) => { if (!cancelled) setUnreadCount(result.unread_count); })
      .catch((error) => { if (!cancelled) console.error('Notification badge refresh failed:', error); });
    return () => { cancelled = true; };
  }, [authReady, clearUnreadCount, isLogin, setUnreadCount]);

  const getPageTitle = (p: string): string => {
    if (pageTitles[p]) return pageTitles[p];
    if (p.startsWith('/chat/')) return langData.casefile_title;
    for (const [route, title] of Object.entries(pageTitles)) {
      if (p.startsWith(route)) return title;
    }
    return '';
  };

  const pageTitle = getPageTitle(basePathname);
  const discoverHref = localizedPublicPath('/discover', languageCode);

  function handleLanguageChange(nextLanguageCode: string) {
    void updateLanguageCode(nextLanguageCode);
  }

  return (
    <header
      className={cn(
        'mobile-topbar-safe sticky top-0 z-30 flex h-[64px] items-center justify-between px-4 sm:px-6',
        'border-b border-[var(--border)]',
      )}
      style={{
        backgroundColor: theme === 'dark' ? 'rgba(8, 8, 8, 0.85)' : 'rgba(245, 244, 240, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      {/* ── Left: brand on mobile, page title on tablet+ ── */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Brand: shown only when Sidebar is hidden (<640px) */}
        <Link
          href={discoverHref}
          className="flex items-center gap-2 sm:hidden"
        >
          <Image src="/app_icon_1024.png" alt="境谜" width={28} height={28} className="rounded-md shrink-0" />
          <span
            className="hidden truncate text-[15px] tracking-[1.5px] text-[var(--lamp)] min-[360px]:inline"
            style={{ fontFamily: 'var(--font-playfair), "Playfair Display", serif' }}
          >
            {langData.login_brand}
          </span>
        </Link>
        {/* Page title: shown on tablet+ */}
        <h2
          className="hidden sm:block text-[14px] uppercase tracking-[2px] text-gray-400"
          style={{ fontFamily: 'var(--font-playfair), "Playfair Display", serif' }}
        >
          {pageTitle}
        </h2>
      </div>

      {/* ── Right: actions ── */}
      <div className="flex items-center gap-1">
        <div ref={communityRef} className="relative">
          <button
            type="button"
            onClick={() => setCommunityOpen((open) => !open)}
            aria-expanded={communityOpen}
            aria-controls="community-menu"
            aria-label={langData.topbar_community_join}
            className={cn(
              'flex h-[36px] items-center gap-1.5 rounded-sm px-2 text-xs text-gray-400 transition-colors',
              'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
            )}
          >
            <UsersRound className="h-[15px] w-[15px]" />
            <span className="hidden md:inline">{langData.topbar_community_join}</span>
          </button>
          {communityOpen && (
            <div
              id="community-menu"
              className="mobile-topbar-menu fixed inset-x-3 z-50 max-h-[calc(100dvh-72px)] w-auto overflow-y-auto rounded-sm border border-[var(--border)] p-2 shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-[calc(100%+8px)] sm:w-[280px]"
              style={{ background: 'var(--card)' }}
            >
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="https://discord.gg/uYay26rPaX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex min-h-[124px] flex-col items-center justify-center gap-2 rounded-sm border border-[#5865F2]/30 bg-[#5865F2]/[0.08] px-2 py-4 text-[#5865F2] transition-colors hover:bg-[#5865F2]/[0.16] sm:aspect-square sm:min-h-0"
                >
                  <MessageCircle className="h-7 w-7" />
                  <span className="text-xs font-medium">Discord</span>
                  <span className="text-[10px] text-[#5865F2]/70">Scenigma</span>
                  <ExternalLink className="h-3 w-3 opacity-50 transition-opacity group-hover:opacity-100" />
                </a>
                {qqGroupUrl ? (
                  <a
                    href={qqGroupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex min-h-[124px] flex-col items-center justify-center gap-2 rounded-sm border border-sky-400/30 bg-sky-400/[0.08] px-2 py-4 text-sky-400 transition-colors hover:bg-sky-400/[0.16] sm:aspect-square sm:min-h-0"
                  >
                    <UsersRound className="h-7 w-7" />
                    <span className="text-xs font-medium">{langData.topbar_community_qq}</span>
                    <span className="text-center text-[10px] leading-tight text-sky-400/75">Scenigma</span>
                    <ExternalLink className="h-3 w-3 opacity-50 transition-opacity group-hover:opacity-100" />
                  </a>
                ) : (
                  <div className="flex min-h-[124px] flex-col items-center justify-center gap-2 rounded-sm border border-border bg-muted/20 px-2 py-4 text-muted-foreground/60 sm:aspect-square sm:min-h-0" title={langData.topbar_community_pending}>
                    <UsersRound className="h-7 w-7" />
                    <span className="text-center text-xs font-medium">{langData.topbar_community_qq}</span>
                    <span className="text-center text-[10px] leading-tight">Scenigma</span>
                    <span className="text-center text-[9px] leading-tight">{langData.topbar_community_pending}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Language selector */}
        <div className="relative">
          <Languages
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[15px] w-[15px] -translate-x-1/2 -translate-y-1/2 text-gray-400 sm:left-2 sm:translate-x-0"
          />
          <select
            value={languageCode}
            onChange={(e) => { void handleLanguageChange(e.target.value); }}
            aria-label="Language"
            title={LANGUAGE_OPTIONS.find((language) => language.id === languageCode)?.label}
            className={cn(
              'h-[36px] w-[36px] appearance-none rounded-sm border border-transparent bg-transparent p-0 text-xs text-transparent',
              'cursor-pointer transition-colors hover:border-[var(--border)] hover:text-transparent',
              'sm:w-auto sm:py-1 sm:pl-7 sm:pr-2 sm:text-gray-400 sm:hover:text-[var(--foreground)]',
              'focus:outline-none',
            )}
          >
            {LANGUAGE_OPTIONS.map(lang => (
              <option key={lang.id} value={lang.id} className="bg-[var(--card)] text-foreground">
                {lang.label}
              </option>
            ))}
          </select>
        </div>
        {/* Theme toggle */}
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className={cn(
            'flex h-[36px] w-[36px] items-center justify-center rounded-sm',
            'text-gray-400 transition-colors hover:text-[var(--foreground)] hover:bg-[var(--accent)]',
          )}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <Sun className="h-[18px] w-[18px]" />
          ) : (
            <Moon className="h-[18px] w-[18px]" />
          )}
        </button>

        {/* Notification bell */}
        <button
          type="button"
          onClick={onOpenNotifications}
          className={cn(
            'relative flex h-[36px] w-[36px] items-center justify-center rounded-sm',
            'text-gray-400 transition-colors hover:text-[var(--foreground)] hover:bg-[var(--accent)]',
          )}
          aria-label={getNotificationCopy(languageCode, langData).notif_page_title}
          aria-controls="notification-drawer"
          aria-expanded={notificationDrawerOpen}
        >
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && <span className="absolute right-1 top-1 min-w-4 rounded-full bg-red-500 px-1 text-center text-[9px] leading-4 text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>

        {/* User avatar */}
        {isLogin && (
          <Link href="/profile" className="flex items-center gap-2 ml-1 px-2 h-[36px] rounded-sm hover:bg-[var(--accent)] transition-colors">
            <div className="w-[26px] h-[26px] rounded-full border border-[var(--border)] overflow-hidden flex items-center justify-center bg-[var(--card)] shrink-0">
              {userProfile.avatar
                ? <img src={userProfile.avatar} alt="" className="w-full h-full object-cover" />
                : <User className="w-[14px] h-[14px] text-gray-400" />}
            </div>
            <span className="hidden sm:block text-xs text-[var(--foreground)] max-w-[80px] truncate">
              {userProfile.username || 'Detective'}
            </span>
          </Link>
        )}
        {authReady && !isLogin && (
          <Link
            href="/login"
            className="ml-1 flex h-[36px] items-center gap-2 rounded-sm border border-lamp/25 bg-lamp-dim px-2.5 text-lamp transition-colors hover:border-lamp/45 hover:bg-lamp-glow"
            aria-label={langData.topbar_login_account}
          >
            <User className="h-[15px] w-[15px] shrink-0" />
            <span className="hidden min-w-0 flex-col text-left leading-none sm:flex">
              <span className="truncate text-[11px] font-medium">{langData.topbar_login_account}</span>
              <span className="mt-1 truncate text-[9px] text-lamp/65">{langData.topbar_login_bonus}</span>
            </span>
          </Link>
        )}
      </div>
    </header>
  );
}
